import { clientFetch } from "@app/lib/egress/client";
import { getErrorFromResponse } from "@app/lib/swr/swr";
import { useUserMetadata } from "@app/lib/swr/user";
import type { PostCosAgentNameResponseBody } from "@app/types/api/me/cos_agent_name";
import { resolveCosAgentName } from "@app/types/cos_agent_name";
import { cosAgentNameMetadataKey } from "@app/types/onboarding";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";

export function useCosAgentName() {
  const { metadata, isMetadataLoading, mutateMetadata } = useUserMetadata(
    cosAgentNameMetadataKey
  );
  return {
    agentName: resolveCosAgentName(metadata?.value),
    storedAgentName: metadata?.value ?? null,
    isAgentNameLoading: isMetadataLoading,
    mutateAgentName: mutateMetadata,
  };
}

export function useSaveCosAgentName({ owner }: { owner: LightWorkspaceType }) {
  const { mutateAgentName } = useCosAgentName();

  return useCallback(
    async (name: string): Promise<Result<string, string>> => {
      const res = await clientFetch(`/api/w/${owner.sId}/me/agent-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        return new Err((await getErrorFromResponse(res)).message);
      }
      const body: PostCosAgentNameResponseBody = await res.json();
      await mutateAgentName();
      return new Ok(body.agentName);
    },
    [mutateAgentName, owner.sId]
  );
}
